import { Link } from 'react-router-dom'
import { ArrowLeft, Mail, Heart, Building2, Truck, Users } from 'lucide-react'
import { useState } from 'react'
import { volunteerImages } from '@/assets/images'
import { submitFeedback } from '../api/feedback'

const ContactPage = () => {
  const [userType, setUserType] = useState('')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(false)

    try {
      await submitFeedback({
        name: formData.name,
        email: formData.email,
        userType: userType || undefined,
        message: formData.message
      })
      
      setSubmitSuccess(true)
      setFormData({ name: '', email: '', message: '' })
      setUserType('')
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000)
    } catch (error) {
      console.error('Error submitting feedback:', error)
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit feedback. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-primary-50">
      {/* Hero Section */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <Link
              to="/"
              className="inline-flex items-center text-gray-600 hover:text-primary-600 mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            
            <div className="flex justify-center mb-6">
              <img 
                src="/harvesthelp-logo.png"
                alt="HarvestHelp Logo" 
                className="h-16 w-auto"
              />
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Contact Us</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get in touch with Garrett Zhou, the creator of HarvestHelp
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Contact Information */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="relative mb-6">
                                 <img 
                   src={volunteerImages.me} 
                   alt="Garrett Zhou" 
                   className="w-64 h-64 object-cover rounded-full mx-auto shadow-lg"
                 />
                <div className="absolute -bottom-2 -right-2 bg-primary-600 rounded-full p-3">
                  <Heart className="h-6 w-6 text-white" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Garrett Zhou</h2>
              <p className="text-lg text-gray-600 mb-4">Creator of HarvestHelp</p>
              <p className="text-gray-600">
                High School Sophomore at Durham Academy, passionate about fighting hunger and reducing food waste.
              </p>
            </div>

            <div className="space-y-4">
              <a
                href="mailto:garrettzhou09@gmail.com"
                className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
              >
                <div className="bg-primary-100 rounded-full p-3 mr-4 group-hover:bg-primary-200 transition-colors">
                  <Mail className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Email</div>
                  <div className="text-gray-600">garrettzhou09@gmail.com</div>
                </div>
              </a>
            </div>

            <div className="pt-6 border-t border-gray-200 mt-6">
              <h4 className="font-semibold text-gray-900 mb-3 text-center">Connect With Me</h4>
              <div className="flex flex-wrap justify-center gap-3">
                <a
                  href="https://globalhungerdashboard.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg inline-flex items-center transition-colors"
                >
                  Global Hunger Dashboard
                </a>
                <a
                  href="https://garrbearsblog.wordpress.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg inline-flex items-center transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2c5.514 0 10 4.486 10 10s-4.486 10-10 10S2 17.514 2 12 6.486 2 12 2zm-1 3v8h8v-2h-6V5h-2z"/>
                  </svg>
                  Garrett&apos;s Personal Blog
                </a>
              </div>
            </div>
          </div>

          {/* Right: Feedback Form */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Feedback</h2>
            <p className="text-gray-600 mb-6">
              I'd love to hear from you! Whether you have suggestions, questions, or just want to share your experience with HarvestHelp, please let me know.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* User Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  I am a: <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setUserType('FOOD_BANK')}
                    className={`p-4 rounded-lg border-2 transition-colors ${
                      userType === 'FOOD_BANK'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 hover:border-primary-300'
                    }`}
                  >
                    <Building2 className="h-6 w-6 mx-auto mb-2" />
                    <div className="text-sm font-medium">Food Bank</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setUserType('SUPPLIER')}
                    className={`p-4 rounded-lg border-2 transition-colors ${
                      userType === 'SUPPLIER'
                        ? 'border-secondary-500 bg-secondary-50 text-secondary-700'
                        : 'border-gray-200 hover:border-secondary-300'
                    }`}
                  >
                    <Truck className="h-6 w-6 mx-auto mb-2" />
                    <div className="text-sm font-medium">Supplier</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setUserType('VOLUNTEER')}
                    className={`p-4 rounded-lg border-2 transition-colors ${
                      userType === 'VOLUNTEER'
                        ? 'border-accent-500 bg-accent-50 text-accent-700'
                        : 'border-gray-200 hover:border-accent-300'
                    }`}
                  >
                    <Users className="h-6 w-6 mx-auto mb-2" />
                    <div className="text-sm font-medium">Volunteer</div>
                  </button>
                </div>
              </div>

              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Your name"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="your.email@example.com"
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Tell me about your experience, suggestions, or questions..."
                />
              </div>

              {/* Error Message */}
              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
                  {submitError}
                </div>
              )}

              {/* Success Message */}
              {submitSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800 text-sm">
                  Thank you for your feedback! I'll get back to you soon.
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!userType || !formData.name || !formData.email || !formData.message || isSubmitting}
                className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12">
          <Link
            to="/"
            className="btn-primary inline-flex items-center text-lg px-8 py-3"
          >
            <img 
              src="/harvesthelp-logo.png"
              alt="HarvestHelp Logo" 
              className="mr-2 h-5 w-auto"
            />
            Back to Home
            <ArrowLeft className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>
    </div>
  )
}

export default ContactPage
